// src/utils/cacheManager.js
import logger from './logger';

/**
 * Cache Manager Utility
 * 
 * Provides sessionStorage-based caching for:
 * - Device state (30-second TTL)
 * - Installation sync timestamps (30-second TTL)
 * 
 * Extracted from useDeviceOperations hook (Phase 3D PR #3)
 */

// Cache TTL constants
const DEVICE_STATE_CACHE_TTL_MS = 30 * 1000; // 30 seconds
const INSTALLATION_SYNC_CACHE_TTL_MS = 30 * 1000; // 30 seconds

/**
 * Device State Cache Functions
 */

/**
 * Get cached device state from sessionStorage
 * @param {string} deviceId - Device ID
 * @returns {Object|null} Cached device state or null if not found/expired
 */
export const getDeviceStateCache = (deviceId) => {
  if (!deviceId) return null;

  const cacheKey = `device_state_${deviceId}`;
  const cacheTimeKey = `device_state_${deviceId}_timestamp`;

  try {
    const cachedData = sessionStorage.getItem(cacheKey);
    const cacheTimestamp = sessionStorage.getItem(cacheTimeKey);

    if (cachedData && cacheTimestamp) {
      const age = Date.now() - parseInt(cacheTimestamp, 10);
      if (age < DEVICE_STATE_CACHE_TTL_MS) {
        logger.log(
          `Using cached state for device ${deviceId} (age: ${Math.round(
            age / 1000
          )}s)`
        );
        return JSON.parse(cachedData);
      } else {
        logger.log(
          `Cache expired for device ${deviceId} (age: ${Math.round(
            age / 1000
          )}s)`
        );
      }
    }
  } catch (error) {
    logger.warn('Cache read error:', error);
  }

  return null;
};

/**
 * Set device state in sessionStorage cache
 * @param {string} deviceId - Device ID
 * @param {Object} data - Device state data to cache
 */
export const setDeviceStateCache = (deviceId, data) => {
  if (!deviceId) return;

  const cacheKey = `device_state_${deviceId}`;
  const cacheTimeKey = `device_state_${deviceId}_timestamp`;

  try {
    sessionStorage.setItem(cacheKey, JSON.stringify(data));
    sessionStorage.setItem(cacheTimeKey, Date.now().toString());
    logger.log(`Cached state for device ${deviceId}`);
  } catch (error) {
    logger.warn('Cache write error:', error);
  }
};

/**
 * Invalidate device state cache
 * @param {string} deviceId - Device ID
 */
export const invalidateDeviceStateCache = (deviceId) => {
  if (!deviceId) return;

  try {
    const cacheKey = `device_state_${deviceId}`;
    const cacheTimeKey = `device_state_${deviceId}_timestamp`;
    sessionStorage.removeItem(cacheKey);
    sessionStorage.removeItem(cacheTimeKey);
    logger.log(`Invalidated cache for device ${deviceId}`);
  } catch (error) {
    logger.warn(`Failed to invalidate cache for device ${deviceId}:`, error);
  }
};

/**
 * Installation Sync Cache Functions
 */

/**
 * Get installation sync cache status
 * @param {string} installationId - Installation ID
 * @returns {Object|null} Cache info {timestamp, age, isValid} or null if not found/expired
 */
export const getInstallationSyncCache = (installationId) => {
  if (!installationId) return null;

  try {
    const cacheTimeKey = `installation_sync_${installationId}_timestamp`;
    const cachedTimestamp = sessionStorage.getItem(cacheTimeKey);

    if (cachedTimestamp) {
      const age = Date.now() - parseInt(cachedTimestamp, 10);
      if (age < INSTALLATION_SYNC_CACHE_TTL_MS) {
        logger.log(
          `[InstallationSyncCache] ✅ Cache valid for installation ${installationId} (age: ${Math.round(
            age / 1000
          )}s)`
        );
        return {
          timestamp: parseInt(cachedTimestamp, 10),
          age,
          isValid: true,
        };
      } else {
        logger.log(
          `[InstallationSyncCache] ❌ Cache expired for installation ${installationId} (age: ${Math.round(
            age / 1000
          )}s)`
        );
      }
    }
  } catch (error) {
    logger.warn('[InstallationSyncCache] Cache read error:', error);
  }

  return null;
};

/**
 * Set installation sync cache timestamp
 * @param {string} installationId - Installation ID
 */
export const setInstallationSyncCache = (installationId) => {
  if (!installationId) return;

  try {
    const cacheTimeKey = `installation_sync_${installationId}_timestamp`;
    sessionStorage.setItem(cacheTimeKey, Date.now().toString());
    logger.log(
      `[InstallationSyncCache] 📝 Cached sync timestamp for installation ${installationId}`
    );
  } catch (error) {
    logger.warn('[InstallationSyncCache] Cache write error:', error);
  }
};

/**
 * Invalidate installation sync cache
 * @param {string} installationId - Installation ID
 */
export const invalidateInstallationSyncCache = (installationId) => {
  if (!installationId) return;

  try {
    const cacheTimeKey = `installation_sync_${installationId}_timestamp`;
    sessionStorage.removeItem(cacheTimeKey);
    logger.log(
      `[InstallationSyncCache] 🗑️  Invalidated cache for installation ${installationId}`
    );
  } catch (error) {
    logger.warn('[InstallationSyncCache] Cache invalidation error:', error);
  }
};

/**
 * Cache TTL constants (exported for testing/reference)
 */
export const CACHE_TTL = {
  DEVICE_STATE: DEVICE_STATE_CACHE_TTL_MS,
  INSTALLATION_SYNC: INSTALLATION_SYNC_CACHE_TTL_MS,
};
